import { del, generateQueryString, get, post, put } from '@/utils';
import { AxiosError } from 'axios';
import i18n from '@/i18n';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import {
	IAnnouncementCreatePayload,
	IAnnouncementListItem,
	IAnnouncementsListQuery,
	IAnnouncementsListResponse,
	IAnnouncementUpdatePayload
} from './types';

// GET api/announcements — listă paginată cu filtre (Search / ProjectId / StatusId / TypeId / PriorityId).
async function fetchAnnouncementsList(
	query: IAnnouncementsListQuery,
	page: number,
	items: number,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<IAnnouncementListItem[]> {
	try {
		const queryString = generateQueryString({
			...query,
			PageNumber: page + 1,
			PageSize: items
		});
		const res = await get(`api/announcements${queryString}`);
		const data = res.data as IAnnouncementsListResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return data.items || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorLoad'));
	}
}

// GET api/announcements/:id → detaliul unui anunț (aceeași formă ca elementele listei).
async function fetchSingleAnnouncement(
	announcementId: number | string
): Promise<IAnnouncementListItem> {
	try {
		const res = await get(`api/announcements/${announcementId}`);
		return res.data as IAnnouncementListItem;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorNotFound'));
	}
}

// POST api/announcements (AnnouncementCreateDto) — orice 2xx = succes.
async function postAnnouncement(
	payload: IAnnouncementCreatePayload
): Promise<string | number> {
	try {
		const res = await post('api/announcements', { ...payload });
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorCreate'));
	}
}

// UPDATE api/announcements/:id — răspunde cu anunțul actualizat (aceeași formă ca detaliul).
async function patchAnnouncement(
	announcementId: number | string,
	payload: IAnnouncementUpdatePayload
): Promise<IAnnouncementListItem> {
	try {
		const res = await put(`api/announcements/${announcementId}`, { ...payload });
		return res.data as IAnnouncementListItem;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorUpdate'));
	}
}

// POST api/announcements/:id/publish — răspunde cu anunțul actualizat.
async function postPublishAnnouncement(
	announcementId: number | string
): Promise<IAnnouncementListItem> {
	try {
		const res = await post(`api/announcements/${announcementId}/publish`);
		return res.data as IAnnouncementListItem;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorPublish'));
	}
}

// POST api/announcements/:id/archive — răspunde cu anunțul actualizat.
async function postArchiveAnnouncement(
	announcementId: number | string
): Promise<IAnnouncementListItem> {
	try {
		const res = await post(`api/announcements/${announcementId}/archive`);
		return res.data as IAnnouncementListItem;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorArchive'));
	}
}

// DELETE api/announcements/:id — succes = 200–204.
async function deleteAnnouncement(
	announcementId: number | string
): Promise<string | number> {
	try {
		const res = await del(`api/announcements/${announcementId}`);
		return res?.status && res.status >= 200 && res.status <= 204 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorDelete'));
	}
}

// GET api/announcements/table-config — configurația coloanelor tabelului Anunțuri
// (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează în pagină
// cu definițiile locale prin applyServerColumnConfig.
async function fetchAnnouncementsTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/announcements/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcements.errorTableConfig'));
	}
}

export {
	deleteAnnouncement,
	fetchAnnouncementsTableConfig, fetchAnnouncementsList,
	fetchSingleAnnouncement, patchAnnouncement, postAnnouncement, postArchiveAnnouncement, postPublishAnnouncement
};

